import React, { useState } from "react";
import {
  ExternalLink,
  Smartphone,
  Figma as FigmaIcon,
  Globe,
  FileText,
} from "lucide-react";

const shot = (url) =>
  `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=800&h=520`;

const typeMeta = {
  website: { label: "Website", Icon: Globe },
  figma: { label: "Figma UI/UX", Icon: FigmaIcon },
  "mobile-app": { label: "Mobile App", Icon: Smartphone },
  seo: { label: "SEO Case Study", Icon: FileText },
};

export const ProjectCard = ({ project, onOpen, index = 0 }) => {
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const { label, Icon } = typeMeta[project.type] || typeMeta.website;
  const isSeoCaseStudy = project.type === "seo" || Boolean(project.pdf);

  const canShowScreenshot =
    project.type === "website" &&
    project.hideScreenshot !== true &&
    !screenshotFailed &&
    Boolean(project.url);
  const canShowCover = isSeoCaseStudy && project.cover && !coverFailed;

  const platformBadge = project.platform && (
    <span className="rounded-full border border-white bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--brand-navy))]">
      {project.platform}
    </span>
  );

  return (
    <article
      data-testid={`project-card-${project.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-black/5 bg-white card-shadow transition-all duration-500 hover:-translate-y-1 hover:card-shadow-hover"
      style={{ animation: `fadeUp .5s ${index * 40}ms both` }}
    >
      <button
        type="button"
        onClick={() => onOpen(project)}
        className="relative block aspect-[16/10] w-full overflow-hidden bg-lilac"
        aria-label={`Preview ${project.name}`}
      >
        {canShowCover ? (
          <img
            src={project.cover}
            alt={`${project.name} SEO case study cover`}
            onError={() => setCoverFailed(true)}
            loading="lazy"
            className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
          />
        ) : canShowScreenshot ? (
          <img
            src={shot(project.url)}
            alt={`${project.name} website preview`}
            onError={() => setScreenshotFailed(true)}
            loading="lazy"
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="brand-gradient relative flex h-full w-full items-center justify-center p-6 text-center">
            <div className="relative z-10">
              <Icon className="mx-auto mb-3 h-9 w-9 text-white/90" />
              <div className="font-[Fraunces] text-2xl font-bold text-white">{project.name}</div>
              <div className="mt-1 text-xs text-white/80">{project.domain}</div>
            </div>
            <div className="noise-overlay absolute inset-0" />
          </div>
        )}

        <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur">
            <Icon className="h-3 w-3" /> {label}
          </span>
          {platformBadge}
        </div>
      </button>

      <div className="flex flex-1 flex-col p-5">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-[hsl(var(--brand-navy))]">{project.name}</h3>
          <p className="mt-0.5 truncate text-xs text-slate-500">{project.niche}</p>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {(project.categories || []).slice(0, 2).map((category) => (
            <span key={category} className="rounded-full bg-lilac px-2 py-0.5 text-[10px] font-medium text-brand-deep">{category}</span>
          ))}
          {[...(project.frontend || []), ...(project.backend || [])].slice(0, 2).map((technology) => (
            <span key={technology} className="rounded-full bg-[hsl(var(--brand-amber)/.15)] px-2 py-0.5 text-[10px] font-medium text-amber-brand">{technology}</span>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-black/5 pt-4">
          <button
            type="button"
            onClick={() => onOpen(project)}
            data-testid={`project-details-${project.slug}`}
            className="text-xs font-semibold text-brand-deep hover:text-brand"
          >View details</button>

          {isSeoCaseStudy ? (
            <a href={project.pdf} target="_blank" rel="noopener noreferrer" data-testid={`project-visit-${project.slug}`} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-navy))] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand">
              View Report <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <a href={project.url || "#"} target="_blank" rel="noopener noreferrer" data-testid={`project-visit-${project.slug}`} onClick={(e) => { if (!project.url) e.preventDefault(); }} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-navy))] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand">
              {project.type === "website" ? "View Live" : project.type === "figma" ? "View Design" : "View App"}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
